<template>
  <div class="text-center">
    <v-menu v-model="menu" :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-chip
          :color="readableName() ? 'primary' : 'primary'"
          v-bind="props"
          label
          data-testid="activator"
        >
          {{ $t("common.action.metadata") }}
          <span v-if="readableName()">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold" data-testid="readable-name"> {{ readableName() }}</span>
          <v-btn
            v-if="readableName()"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset(true)"
          />
        </v-chip>
      </template>

      <v-card min-width="450" class="mt-2">
        <v-list>
          <v-list-item :title="$t('page.payment.filter.filterByMetadata')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1">
          <!-- select all and reset -->
          <v-row class="mx-4" no-gutters>
            <v-btn
              variant="text"
              size="x-small"
              color="primary"
              @click="selectAll()"
              data-testid="select-all"
              >{{ $t("common.action.selectAll") }}</v-btn
            >
            <v-btn
              variant="text"
              size="x-small"
              color="primary"
              @click="reset(false)"
              data-testid="reset"
              >| {{ $t("common.action.reset") }}</v-btn
            ></v-row
          >
          <!-- location selection -->
          <v-row class="mx-4" no-gutters>
            <v-col class="d-flex justify-start" cols="1">
              <v-checkbox
                v-model="isLocationSelected"
                color="primary"
                data-testid="location-checkbox"
              />
            </v-col>
            <v-col cols="4">
              <v-list-item class="mt-2" :subtitle="$t('page.locations.location')"
            /></v-col>
            <v-col>
              <v-select
                class="pt-3"
                v-model="locationSelect"
                :items="locations"
                item-title="display_name"
                item-value="id"
                data-testid="location-select"
            /></v-col>
          </v-row>
          <!-- terminal selection -->
          <v-row class="mx-4" no-gutters>
            <v-col class="d-flex justify-start" cols="1">
              <v-checkbox
                v-model="isTerminalSelected"
                color="primary"
                data-testid="terminal-checkbox"
              />
            </v-col>
            <v-col cols="4">
              <v-list-item class="mt-2" :subtitle="$t('page.terminals.terminal')"
            /></v-col>
            <v-col>
              <v-select
                class="pt-3"
                v-model="terminalSelect"
                :items="terminals"
                item-title="label"
                item-value="id"
                data-testid="terminal-select"
            /></v-col>
          </v-row>
          <!-- payment link metadata filter -->
          <v-row class="mx-4" no-gutters>
            <v-col class="d-flex justify-start" cols="1">
              <v-checkbox
                v-model="isPaymentLinkSelected"
                color="primary"
                data-testid="paymentLink-checkbox"
              />
            </v-col>
            <v-col cols="4">
              <v-list-item class="mt-2" :subtitle="$t('common.action.paymentLink')" />
            </v-col>
            <v-col>
              <v-combobox
                class="pt-1"
                v-model="paymentLinkSelect"
                :items="links?.data"
                item-title="name"
                item-value="id"
              />
            </v-col>
          </v-row>
          <!-- product filter -->
          <!-- <v-row class="mx-4" no-gutters>
            <v-col class="d-flex justify-start" cols="1">
              <v-checkbox
                v-model="isProductIdSelected"
                color="primary"
                data-testid="productId-checkbox"
              />
            </v-col>
            <v-col cols="4">
              <v-list-item
                class="mt-2"
                :subtitle="$t('page.payment.filter.product')"
            /></v-col>
            <v-col>
              <v-combobox
                class="pt-1"
                v-model="productSelect"
                :items="products"
                item-title="name"
                item-value="id"
              />
            </v-col>
          </v-row> -->
          <!-- price filter -->
          <!-- <v-row class="mx-4" no-gutters>
            <v-col class="d-flex justify-start" cols="1">
              <v-checkbox
                v-model="isPriceIdSelected"
                color="primary"
                data-testid="priceId-checkbox"
              />
            </v-col>
            <v-col cols="4">
              <v-list-item
                class="mt-2"
                :subtitle="$t('page.payment.filter.price')"
            /></v-col>
            <v-col>
              <v-combobox
                class="pt-1"
                v-model="priceSelect"
                :items="customizedPrices"
                item-title="title"
                item-value="id"
              />
            </v-col>
          </v-row> -->
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              menu = false;
              updateFilterPayments();
            "
            color="primary"
            class="mr-2"
            data-testid="apply-filter"
          >
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from "vue";
import { useLocationStore } from "@/terminals/stores/locations";
import { usePaymentStore } from "@/payments/stores/payments";
import { useReaderStore } from "@/terminals/stores/readers";
import { usePaymentLinksStore } from "@/payment-links/stores/paymentLinks";
import { useProductsStore } from "@/catalog/stores/products";
import { usePricesStore } from "@/catalog/stores/prices";
import i18n from "@/app/plugins/i18n";
import type { LocationSummary } from "@/terminals/types/locations";
import type { Reader } from "@/terminals/types/readers";
import type { PaymentLinks } from "@/payment-links/types/paymentLinks";
import type { Product } from "@/catalog/types/products";
import type { Price } from "@/catalog/types/prices";

const menu = ref(false);
const { setFilterPaymentCondition } = usePaymentStore();

//location
const locations = ref<LocationSummary[] | undefined>();
const isLocationSelected = ref(false);
const locationSelect = ref("");
//terminal
const terminals = ref<Reader[] | undefined>();
const isTerminalSelected = ref(false);
const terminalSelect = ref("");
//product
const productSelect = ref<Product | null>(null);
const isProductIdSelected = ref(false);
const products = ref<Product[]>([]);
//price
const priceSelect = ref<Price | null>(null);
const isPriceIdSelected = ref(false);
const prices = ref<Price[]>([]);
//payment link
const paymentLinkSelect = ref<PaymentLinks | null>(null);
const isPaymentLinkSelected = ref(false);
const links = ref<{ data?: PaymentLinks[] } | undefined>();

watchEffect(async () => {
  links.value = await usePaymentLinksStore().listActivePaymentLinks();
  //products.value = await useProductsStore().listAllProducts();
  locations.value = await useLocationStore().listLocations(false);
  terminals.value = await useReaderStore().listReaders(true, false, undefined);
  //prices.value = await usePricesStore().listAllPrices();
  locationSelect.value = locations.value?.[0]?.id ?? "";
  terminalSelect.value = terminals.value?.[0]?.id ?? "";
});

const customizedPrices = computed(() => {
  return prices.value?.map((price) => {
    return {
      ...price,
      title: price.nickname || price.id,
    };
  });
});

const filterTerminal = computed(() => usePaymentStore().filterPaymentCondition.terminal || "");
const filterLocation = computed(() => usePaymentStore().filterPaymentCondition.location || "");
const filterProductId = computed(() => usePaymentStore().filterPaymentCondition.productId || "");
const filterPriceId = computed(() => usePaymentStore().filterPaymentCondition.priceId || "");
const filterPaymentLink = computed(
  () => usePaymentStore().filterPaymentCondition.paymentLink || "",
);

const selectAll = () => {
  isLocationSelected.value = true;
  isTerminalSelected.value = true;
  isProductIdSelected.value = true;
  isPriceIdSelected.value = true;
  isPaymentLinkSelected.value = true;
};

const reset = async (clear?: boolean) => {
  isLocationSelected.value = false;
  isTerminalSelected.value = false;
  isProductIdSelected.value = false;
  isPriceIdSelected.value = false;
  productSelect.value = null;
  priceSelect.value = null;
  paymentLinkSelect.value = null;
  isPaymentLinkSelected.value = false;
  const filterPaymentCondition = usePaymentStore().filterPaymentCondition;
  delete filterPaymentCondition.location;
  delete filterPaymentCondition.terminal;
  delete filterPaymentCondition.productId;
  delete filterPaymentCondition.priceId;
  delete filterPaymentCondition.paymentLink;
  if (clear) {
    await usePaymentStore().getFilteredPayments();
  }
  setFilterPaymentCondition(filterPaymentCondition);
};

const updateFilterPayments = () => {
  const oldCondition = usePaymentStore().filterPaymentCondition;
  const newCondition = {
    ...oldCondition,
    location: isLocationSelected.value ? locationSelect.value : "",
    terminal: isTerminalSelected.value ? terminalSelect.value : "",
    productId: isProductIdSelected.value ? productSelect.value?.id : "",
    priceId: isPriceIdSelected.value ? priceSelect.value?.id : "",
    paymentLink: isPaymentLinkSelected.value ? paymentLinkSelect.value?.id : "",
  };
  setFilterPaymentCondition(newCondition);
};

const readableName = () => {
  let name = "";

  if (isLocationSelected.value && filterLocation.value) {
    const selectedLocation = locations.value?.find(
      (location) => location.id === filterLocation.value,
    );
    name = selectedLocation ? selectedLocation.display_name : "";
  }

  if (isTerminalSelected.value && filterTerminal.value) {
    const selectedTerminal = terminals.value?.find(
      (terminal) => terminal.id === filterTerminal.value,
    );
    name +=
      selectedTerminal && name
        ? " |" + selectedTerminal.label
        : selectedTerminal
          ? selectedTerminal.label
          : "";
  }

  if (isProductIdSelected.value && filterProductId.value) {
    const selectedProduct = products.value.find((pro) => pro.id === filterProductId.value);
    name +=
      selectedProduct && name
        ? " |" + selectedProduct?.name
        : ((selectedProduct?.name || filterProductId.value) ?? "");
  }

  if (isPriceIdSelected.value && filterPriceId.value) {
    const selectedPrice = customizedPrices.value.find((pri) => pri.id === filterPriceId.value);
    name += selectedPrice && name ? " |" + selectedPrice?.title : (selectedPrice?.title ?? "");
  }

  if (isPaymentLinkSelected.value && filterPaymentLink.value) {
    const selectedLink = links.value?.data?.find((link) => link.id === filterPaymentLink.value);
    const selectedLinkName = (selectedLink as { name?: string } | undefined)?.name;
    name +=
      selectedLinkName && name
        ? " |" + selectedLinkName
        : ((selectedLinkName || filterPaymentLink.value) ?? "");
  }

  return name;
};
</script>
